import * as React from 'react';
import dayjs from 'dayjs';
import { EVENT_IMAGE } from 'Constants';
import { isNullOrUndefined } from 'Util/TypeGuards';
import alertToast from 'Util/ToastifyUtils';
import { GetFormattedDate, GetFormattedTime } from './EventUtils';
import If from '../../../If/If';
import Icon from '../../Icon/Icon';
import { Link, useNavigate } from 'react-router-dom';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import { store } from 'Models/Store';
import { Button, Colors, Display, Sizes } from '../../../Button/Button';
import { EventOccurrence, EventSeriesWithNextOccurrence } from 'Services/Api/_HumanWritten/EventSeriesEntityService';

export interface EventSeriesCardProps {
	eventSeries: EventSeriesWithNextOccurrence;
	index: number;
}

export function EventSeriesCard(props: EventSeriesCardProps) {
	const { eventSeries, index } = props;
	return (
		<div className="new-event-card__container">
			<EventSeriesCardDetails eventSeries={eventSeries} index={index} />
		</div>
	);
}

function EventSeriesCardDetails({ eventSeries, index }: EventSeriesCardProps) {
	const navigate = useNavigate();

	if (isNullOrUndefined(eventSeries) || !eventSeries.hasUpcomingOccurrence) {
		alertToast('Event series is missing details', 'error', undefined, { autoClose: 3000 });
		return <></>;
	}

	const handleEditClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		navigate(`/upcoming-events/edit/${eventSeries.id}`);
	};

	const handleCheckInClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		navigate(`/event-check-in/${eventSeries.nextOccurrenceId}`);
	};

	// Check if this event has multiple occurrences
	const hasMultipleOccurrences = (eventSeries.upcomingOccurrenceCount ?? 0) > 1;
	const upcomingOccurrences = eventSeries.upcomingOccurrences ?? [];

	// Parse date strings to Date objects for formatting (single occurrence)
	const startDate = eventSeries.startDate ? new Date(eventSeries.startDate) : undefined;
	const endDate = eventSeries.endDate ? new Date(eventSeries.endDate) : undefined;
	const startTime = eventSeries.startTime ? new Date(`1970-01-01T${eventSeries.startTime}`) : undefined;
	const endTime = eventSeries.endTime ? new Date(`1970-01-01T${eventSeries.endTime}`) : undefined;

	return (
		<div className="new-event-details__container">
			<Link
				to={`/event-details/${eventSeries.id}`}
				className="event-details__link hide-underline"
				id={eventSeries.id}
				tabIndex={index}
			>
				<div className="event-details__image">
					<img
						className="event-detail__header-img"
						src={EVENT_IMAGE + eventSeries.headerImage?.id}
						alt="header-img"
					/>
					<p className="event-details__label">
						{eventSeries.eventLabel?.iconName && (
							<Icon classname="event-icon" name={eventSeries.eventLabel.iconName} useMaterialIcons />
						)}
						{`${eventSeries.eventLabel?.label ?? ''} ${eventSeries.hidden ? '(private)' : ''}`}
					</p>
					{/* Only show capacity for single occurrence events */}
					<If condition={!hasMultipleOccurrences}>
						<p className="event-capacity__label">
							<div className="issue-icon white-icon" />
							<If condition={(eventSeries.remainingCapacity ?? 0) === 0}>Sold out</If>
							<If condition={(eventSeries.remainingCapacity ?? 0) > 0 && !eventSeries.closed}>
								Tickets left: {eventSeries.remainingCapacity ?? 0}/{eventSeries.totalCapacity ?? 0}
							</If>
							<If condition={(eventSeries.remainingCapacity ?? 0) > 0 && eventSeries.closed}>Closed</If>
						</p>
					</If>
					<If condition={store.isStaff}>
						<div className="event-card__staff-actions">
							<Button
								className="event-card__staff-btn icon-edit-box icon-only"
								display={Display.Text}
								colors={Colors.None}
								sizes={Sizes.Small}
								onClick={handleEditClick}
							/>
							<If condition={store.can('AccessCheckIn')}>
								<Button
									className="event-card__staff-btn icon-check-box icon-only"
									display={Display.Text}
									colors={Colors.None}
									sizes={Sizes.Small}
									onClick={handleCheckInClick}
								/>
							</If>
						</div>
					</If>
				</div>
				<div className="event-details__body" key={eventSeries.id}>
					<div className="event-details__header">
						<h4 className="event-details__name">{eventSeries.name}</h4>
					</div>
					<p className="event-details__location icon-location-pin icon-left">{eventSeries.address}</p>
					{/* Show date pills for multi-occurrence events */}
					<If condition={hasMultipleOccurrences}>
						<OccurrenceDatePills
							occurrences={upcomingOccurrences}
							totalCount={eventSeries.upcomingOccurrenceCount ?? upcomingOccurrences.length}
						/>
					</If>
					{/* Show single date/time for single occurrence events */}
					<If condition={!hasMultipleOccurrences}>
						<>
							{startDate && endDate && (
								<p className="event-details__date">{GetFormattedDate(startDate, endDate)}</p>
							)}
							{startTime && endTime && (
								<p className="event-details__time">{GetFormattedTime(startTime, endTime)}</p>
							)}
						</>
					</If>
					<p className="event-details__price">From {formatCurrency(eventSeries.fromPrice ?? 0)}</p>
				</div>
			</Link>
		</div>
	);
}

interface OccurrenceDatePillsProps {
	occurrences: EventOccurrence[];
	totalCount?: number;
	maxPills?: number;
}

function OccurrenceDatePills({ occurrences, totalCount, maxPills = 4 }: OccurrenceDatePillsProps) {
	// If we have more than maxPills, show (maxPills - 1) pills + "+ X more"
	// This ensures total items shown is always <= maxPills
	const total = totalCount ?? occurrences.length;
	const hasMore = total > maxPills;
	const pillsToShow = hasMore ? maxPills - 1 : occurrences.length;
	const displayOccurrences = occurrences.slice(0, pillsToShow);
	const remainingCount = total - pillsToShow;

	return (
		<div className="event-details__date-pills">
			{displayOccurrences.map(occ => {
				const date = dayjs(occ.startDate);
				const time = dayjs(`1970-01-01T${occ.startTime}`);
				return (
					<span
						key={occ.id}
						className={`event-details__date-pill ${occ.isSoldOut ? 'event-details__date-pill--sold-out' : ''}`}
					>
						{date.format('D MMM')} {time.format('h:mma')}
					</span>
				);
			})}
			{remainingCount > 0 && (
				<span className="event-details__date-pill event-details__date-pill--more">+{remainingCount} more</span>
			)}
		</div>
	);
}

export default EventSeriesCard;
